<template>
  <BModal
    :modal-name="modalName"
    height="auto"
  >
    <BHeightAdjuster>
      <BModalHeader
        :title="$t('leadStage.importTitle')"
        center
        @modal-close="hide"
      />
      <BModalBody>
        <BLayout
          justify-center
          align-center
          is-fit
        >
          <BFileUploadArea
            ref="fileUploadArea"
            class="mt-400 file-upload-area"
            :accept-data-type="acceptDataType"
            is-fit
            @upload="upload"
          />
        </BLayout>
      </BModalBody>
      <BModalFooter>
        <BLayout justify-center>
          <div class="message">
            <span class="desc">{{ $t('leadStage.importTemplate.desc') }}</span>
            <div class="dl-btn">
              <BBtn
                type="primary"
                @click="dlButton"
              >
                <span>{{ $t('leadStage.importTemplate.download') }}</span>
              </BBtn>
            </div>
            <BBtn
              :loading="loading"
              :disabled="!save"
              type="primary"
              @click="saveButton"
            >
              <span>{{ $t('leadStage.import') }}</span>
            </BBtn>
          </div>
        </BLayout>
      </BModalFooter>
    </BHeightAdjuster>
  </BModal>
</template>

<script lang="ts" setup>
import type { AxiosError } from 'axios';
import { inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Api from '@/api/user';
import { LeadStageApiService } from '@/api/user/resources/lead_stage';
import { useAlert } from '@/composable/message-dialog';
import { KEY_OF_MODAL } from '@/injection-keys';
import { downloadExcel } from '@/utils/file';

type TEmit = (event: 'refresh') => void;

const emit = defineEmits<TEmit>();
const acceptDataType = '.xlsx';
const i18n = useI18n();
const { alert } = useAlert();
const modal = inject(KEY_OF_MODAL);
const modalName = ref('leadStageImportModal');
const loading = ref(false);
const save = ref(false);
const fileBlob = ref<FormData | null>(null);
const fileUploadArea = ref<{ clear: () => void }>();

const upload = async (file: File) => {
  loading.value = true;
  const separatedFileName = file.name.split('.');
  const importFileExtension = `.${separatedFileName[separatedFileName.length - 1]}`;

  if (importFileExtension.toLowerCase() !== acceptDataType) {
    await alert({ message: i18n.t('import.errors.extension') });
    loading.value = false;
    return;
  }
  const MAX_FILE_SIZE = 100000;
  if (file.size > MAX_FILE_SIZE) {
    onFileUploadFailed();
    await alert({ message: i18n.t('import.errors.maxFileSize', { max_file_size: `${MAX_FILE_SIZE / 1000}KB` }) });
    return;
  }

  const formData = new FormData();
  formData.append('file', file);
  fileBlob.value = formData;
  save.value = true;
  loading.value = false;
};

const saveButton = async () => {
  if (!fileBlob.value) {
    await alert({ message: i18n.t('apiError.message.fileUploadError') });
    loading.value = false;
    save.value = false;
    return;
  }
  const leadStageApi = new LeadStageApiService();
  try {
    await leadStageApi.import({
      options: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: fileBlob.value,
      },
    });
    save.value = false;
    loading.value = false;
    emit('refresh');
    hide();
  } catch (err) {
    const axiosError = err as AxiosError<{ errorMessage?: string }>;
    await alert({ title: i18n.t('import.excel.errorTitle'), message: axiosError.response?.data?.errorMessage ?? '' });
    loading.value = false;
  }
};

const dlButton = async () => {
  const fileName = 'lead_stage_import_template.xlsx';
  const res = await Api.leadStagesImportTemplate({});
  downloadExcel(res.data, fileName);
};

const onFileUploadFailed = () => {
  save.value = false;
  loading.value = false;
};

const hide = () => {
  fileBlob.value = null;
  save.value = false;
  fileUploadArea.value?.clear();
  modal?.hide(modalName.value);
};
</script>

<style lang="scss" scoped>
.message {
  padding: $basespace-100 0;
  text-align: center;
  color: $basecolor-primary;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  .desc {
    padding-bottom: $basespace-200;
    color: $concrete-dark;
  }
  .dl-btn {
    padding-bottom: $basespace-500;
  }
}
.file-upload-area {
  width: 100%;
}
</style>
